/**
 * Interface for the CyberSource payload.
 * @typedef {Object} Payload
 * @property {string} card_number - The card number.
 * @property {string} card_holder_name - The card holder name.
 * @property {string} card_exp_month - The card expiration month.
 * @property {string} card_exp_year - The card expiration year.
 * @property {string} card_brand - The card brand.
 * @property {string} card_cvv - The card CVV.
 * @property {Object} credit_card_tokens - The credit card tokens.
 * @property {string} credit_card_tokens.cybersource_token - The CyberSource token.
 */

// Import CardBrands constants
import { CardBrands } from '../../../src/utils/cards';

/**
 * Maps card brands to CyberSource type codes according to official documentation
 * @param {string} cardBrand - The card brand
 * @returns {string} The CyberSource type code
 */
function getCardTypeCode(cardBrand) {
  const cardTypeCodes = {
    [CardBrands.VISA]: '001', // Visa (includes Visa Electron)
    [CardBrands.ELECTRON]: '001', // Visa Electron - use 001 as per documentation
    [CardBrands.MASTERCARD]: '002', // Mastercard, Eurocard
    [CardBrands.AMEX]: '003', // American Express
    [CardBrands.DISCOVER]: '004', // Discover
    [CardBrands.DINERS]: '005', // Diners Club
    [CardBrands.JCB]: '007', // JCB
    [CardBrands.MAESTRO]: '042', // Maestro International
    [CardBrands.UNION_PAY]: '062', // China UnionPay
  };

  return cardTypeCodes[cardBrand]; // Return undefined if unknown, let CyberSource handle it
}

/**
 * Maps the CyberSource response to the expected format.
 * @param {string} token - The token
 * @param {Object} cardParams - The card parameters
 * @returns {Payload} The mapped CyberSource response
 */
function mappedCyberResponse(token, cardParams) {
  return {
    credit_card_tokens: {
      cybersource_transient_token: token,
    },
    card_holder_name: cardParams.holderName,
    card_brand: cardParams.cardBrand,
  };
}

/**
 * CyberSource payment engine factory function.
 * @returns {Object} CyberSource engine instance with payment methods
 */
function CyberSource() {
  let flex;
  let microform;

  /**
   * Load the antifraud beacon used by CyberSource to track the user's behavior.
   * @param {*} param0 - The parameters
   * @param {*} param0.orgId - The organization ID
   * @param {*} param0.sessionId - The session ID
   * @param {*} param0.merchantId - The merchant ID
   *
   * @returns {function} The function to load the antifraud beacon
   */
  function loadAntifraudBeacon({ orgId, sessionId, merchantId }) {
    return () => {
      const url = `//h.online-metrix.net/fp/tags.js?org_id=${orgId}&session_id=${merchantId}${sessionId}`;
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = false;
      s.defer = true;
      s.src = url;
      const x = document.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };
  }

  /**
   * Initializes the CyberSource FLEX SDK.
   * @param {String} scriptSource - The FLEX Microform js file.
   */
  function loadCybersourceFlex(scriptSource) {
    return () => {
      const script = document.createElement('script');
      script.src = scriptSource;
      script.defer = true;
      script.async = false;
      script.id = 'cybersource-flex';
      document.body.appendChild(script);
    };
  }

  /**
   * Initializes the CyberSource FLEX SDK.
   * @param {string} captureContext - The capture context.
   * @returns {void}
   */
  function initializeFlex(captureContext) {
    if (typeof Flex === 'undefined') {
      throw new Error('CyberSource Flex SDK not loaded');
    }
    // eslint-disable-next-line no-undef
    flex = new Flex(captureContext);
    return flex;
  }

  /**
   * Initialize microform
   */
  function initializeMicroform() {
    // This styles are necesary because we need to send it to the cybersource render, other styles are located @ _form.scss
    const styles = {
      input: {
        'font-size': '16px',
      },
      valid: { color: '#3c763d' },
      invalid: { color: '#ff040d' },
    };
    microform = flex.microform({ styles });
    return microform;
  }

  /**
   * Get microform instance
   */
  function getMicroformInstance() {
    return microform;
  }

  /**
   * Returns the CyberSource FLEX SDK instance.
   * @returns {Object} The CyberSource FLEX SDK instance.
   */
  function getCybersourceFlexInstance() {
    return flex;
  }

  /**
   * Initializes the CyberSource SDK with the provided configuration.
   * @param {Object} params - The configuration object.
   * @param {String} params.orgId - The organization ID for CyberSource.
   * @param {String} params.merchantId - The merchant ID for CyberSource.
   * @param {String} params.sessionId - The session ID for the current session.
   */
  function initialize({ orgId, merchantId, sessionId }) {
    if (document.readyState === 'complete') {
      loadAntifraudBeacon({ orgId, sessionId, merchantId })();
    } else if (window.attachEvent) {
      window.attachEvent('onload', loadAntifraudBeacon({ orgId, sessionId, merchantId }));
    } else {
      window.addEventListener('load', loadAntifraudBeacon({ orgId, sessionId, merchantId }), false);
    }
  }

  /**
   * Creates the payload with the token and the card information.
   * @param {*} cardParams
   * @returns {Promise<>} The promise that resolves with the token or rejects with an error.
   */
  function createPayload(cardParams) {
    return new Promise((resolve, reject) => {
      const microform = getMicroformInstance();

      const {
        expirationMonth: cardExpirationMonth,
        expirationYear: cardExpirationYear,
        cardBrand,
      } = cardParams;

      const options = {
        expirationMonth: cardExpirationMonth,
        expirationYear: cardExpirationYear,
      };

      const cardTypeCode = getCardTypeCode(cardBrand);
      if (cardTypeCode) {
        options.type = cardTypeCode;
      }

      microform.createToken(options, (error, token) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(mappedCyberResponse(token, cardParams));
      });
    });
  }

  return {
    initialize,
    createPayload,
    loadAntifraudBeacon,
    getCybersourceFlexInstance,
    loadCybersourceFlex,
    initializeFlex,
    initializeMicroform,
    getMicroformInstance,
  };
}

const cybersource = CyberSource();
export default cybersource;
